import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// @mui/material components
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { makeStyles } from '@mui/styles';

// material ui icons
import Edit from '@mui/icons-material/Edit';
import UsersIcon from '@mui/icons-material/People';

// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import ReactTable from 'components/ReactTable/ReactTable.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/roleEditStyle.js';

// Redux
import {
  useGetClientsQuery,
  useLazyGetClientTenantsQuery,
} from 'api/client.api';
import { useLazyGetClientUsersQuery } from 'api/user.api';
import { selectSessionStatus } from '../../features/session/sessionSlice';

const useStyles = makeStyles(styles);

export default function Users(props) {
  const classes = useStyles();

  const dispatch = useDispatch();
  const sessionStatus = useSelector(selectSessionStatus);
  const nebulaTerraClientID = '000000000000000000000001';
  const { data: clients } = useGetClientsQuery();
  const [triggerGetClientTenants] = useLazyGetClientTenantsQuery();
  const [client, setClient] = useState();
  const [triggerGetClientUsers, { data: users, isSuccess }] =
    useLazyGetClientUsersQuery();

  const capitalize = (text) => {
    return text?.charAt(0).toUpperCase() + text?.slice(1);
  };

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (sessionStatus === 'active' && !initialized && clients.length) {
      setClient(clients[0]);
      setInitialized(true);
    }
  }, [sessionStatus, initialized]);

  useEffect(() => {
    if (sessionStatus === 'active' && initialized) {
      triggerGetClientUsers(client._id).unwrap();
      triggerGetClientTenants(client._id).unwrap();
    }
  }, [sessionStatus, initialized, client]);

  const handleEditUser = (index) => {
    props.history.push('/admin/user', { user: users[index], new: false });
  };

  const addUserHandler = () => {
    const newUser = {
      nt_administrator: client._id === nebulaTerraClientID ? true : false, // to-do: get this id from redux
      status: 'invited',
      full_name: '',
      display_name: '',
      directory_id: '',
      client_id: client._id,
      email_address: '',
      phone_number: null,
      roles: [],
      default_environment: '',
      nt_role: '',
      avatar: '',
      preferred_language: 'en-US',
    };
    props.history.push('/admin/user', { user: newUser, new: true });
  };

  const handleClientChange = (clientId) => {
    const selectedClient = clients.find((client) => client._id === clientId);
    setClient(selectedClient);
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <UsersIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Users</h4>
            <Button
              color='rose'
              onClick={addUserHandler}
              className={classes.registerButton}
            >
              + Add User
            </Button>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={3}>
                <Select
                  variant='standard'
                  id='select-client'
                  className={classes.select}
                  select
                  label='Client'
                  value={client !== undefined ? client._id : ''}
                  onChange={(e) => handleClientChange(e.target.value)}
                >
                  {clients.map((client) => (
                    <MenuItem key={client._id} value={client._id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
                <span>Please select client company</span>
              </GridItem>
              {isSuccess && (
                <GridItem xs={9}>
                  <ReactTable
                    columns={[
                      {
                        Header: 'User Name',
                        accessor: 'name',
                        disableSortBy: true,
                        width: 200,
                      },
                      {
                        Header: 'Email Address',
                        accessor: 'email',
                        disableSortBy: true,
                        width: 200,
                      },
                      {
                        Header: 'Status',
                        accessor: 'status',
                        disableSortBy: true,
                        width: 50,
                      },
                      {
                        Header: 'Actions',
                        accessor: 'actions',
                        disableFilters: true,
                        disableSortBy: true,
                        width: 100,
                        style: {
                          marginBottom: 20,
                        },
                      },
                    ]}
                    data={users?.map((user, key) => {
                      const userRow = {
                        id: key,
                        name: user.full_name,
                        email: user.email_address,
                        status: capitalize(user.status),
                        actions: (
                          <div className='actions-left'>
                            <Button
                              justIcon
                              round
                              simple
                              onClick={() => handleEditUser(key)}
                              color='warning'
                              className='edit'
                            >
                              <Edit />
                            </Button>{' '}
                          </div>
                        ),
                      };
                      return userRow;
                    })}
                    options={{
                      pageSize: users?.length,
                    }}
                  />
                </GridItem>
              )}
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
